import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TEST_ORG_OTP_CODE, type IdType, type IdVerificationMethod } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { Arrow, Check, Info, ShieldCheck } from "../../components/icons.js";
import { useCameraPreview, unlockSpeech, cameraReasonText } from "./InterviewStep.js";
import { useMicLevel } from "./useMicLevel.js";
import { HardwareDiagnostics } from "./HardwareDiagnostics.js";

/** Small white tick for the selected-ID radio (sized inline; the shared Check has no intrinsic size outside a .btn). */
function RadioTick(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="#fff" strokeWidth={3} aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
import { RequestHuman } from "./RequestHuman.js";

/**
 * Candidate · check-in step (step 4 of 5 — "Check-in"). Camera/mic live preview +
 * real hardware diagnostics (client-only, nothing persisted), plus the identity-verification
 * gate configured by the platform: OTP (email code), government ID + liveness, or — when ID
 * verification is off entirely — a plain device-readiness gate with no identity claim at all.
 * When the server reports `simulated:true` on the id_document path (no vendor contract), we
 * say so plainly: a demo check ran, not a real one.
 */

const SEND_THROTTLE_SECONDS = 30; // mirrors CANDIDATE_OTP_SEND_THROTTLE_SECONDS server-side

interface OtpSendResponse {
  ok: boolean;
  sentTo: string;
  /** false → the code exists but its email never reached the send queue. */
  emailQueued: boolean;
  /** 0041 test org → the code is the known constant, shown as a hint below. */
  testOrg: boolean;
}

/** The API's machine-readable `error` code, when the failure came from the API at all. */
function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as { error?: unknown } | undefined;
  return typeof body?.error === "string" ? body.error : null;
}

/**
 * How a failed "send me a code" should be presented.
 *
 * `wait`/`retry` are worth another attempt; `terminal` is not, and saying "please retry" in front
 * of one is the specific lie this replaces — it left candidates hammering a button that could
 * never work, with no hint that only a human could unblock them. Every terminal case therefore
 * carries a WAY OUT rather than just a reason.
 */
type SendWayOut = "recruiter" | "recover" | "refresh";
interface SendFailure {
  kind: "wait" | "retry" | "terminal";
  message: string;
  wayOut?: SendWayOut;
}

function describeSendFailure(error: unknown): SendFailure {
  if (!(error instanceof ApiError)) {
    return {
      kind: "retry",
      message: "We couldn't reach the server just now — check your connection and try again.",
    };
  }
  if (error.status === 429) {
    return { kind: "wait", message: "Please wait a moment before requesting another code." };
  }
  switch (apiErrorCode(error)) {
    case "otp_no_recipient":
      return {
        kind: "terminal",
        wayOut: "recruiter",
        message:
          "There's no email address on your invitation, so there is nowhere for us to send a code. " +
          "Retrying won't change that — your recruiter needs to add your address.",
      };
    case "otp_not_enabled":
      return {
        kind: "terminal",
        wayOut: "recruiter",
        message:
          "Email code verification isn't switched on for this interview, so no code can be sent. " +
          "Your recruiter needs to sort this out before you can check in.",
      };
    case "otp_already_checked_in":
      return {
        kind: "terminal",
        wayOut: "refresh",
        message: "You've already checked in for this interview — no new code is needed.",
      };
    case "otp_needs_consent":
      return {
        kind: "terminal",
        wayOut: "refresh",
        message: "You need to agree to the interview terms before a code can be sent.",
      };
    case "not_found":
      return {
        kind: "terminal",
        wayOut: "recover",
        message: "This interview link is no longer valid, so we can't send a code to it.",
      };
    default:
      return {
        kind: "retry",
        message:
          error.status >= 500
            ? "Something went wrong on our side and no code was sent. Please try again."
            : "We couldn't send a code just now. Please try again.",
      };
  }
}

/** The concrete escape hatch for a terminal failure — never a dead end, never a bare apology. */
function WayOut({ wayOut }: { wayOut: SendWayOut }): ReactNode {
  if (wayOut === "refresh") {
    return (
      <button className="btn sm ghost" onClick={() => window.location.reload()}>
        Refresh this page
      </button>
    );
  }
  if (wayOut === "recover") {
    return (
      <Link className="btn sm ghost" to="/find-my-link">
        Get a fresh link
      </Link>
    );
  }
  return (
    <span className="sm">
      Reply to the message your invitation arrived in, or request a human interview below.
    </span>
  );
}

interface CheckinCheck {
  label: string;
  verdict: string;
  detail?: string;
}
interface CheckinResponse {
  checks: CheckinCheck[];
  simulated?: boolean;
  idType?: IdType;
}

const ID_LABEL: Record<IdType, { label: string; hint: string }> = {
  aadhaar_digilocker: { label: "DigiLocker / Aadhaar", hint: "Verify instantly via DigiLocker" },
  pan: { label: "PAN card", hint: "Permanent Account Number" },
  driving_license: { label: "Driving licence", hint: "State-issued licence" },
  passport: { label: "Passport", hint: "Machine-readable passport" },
  voter_id: { label: "Voter ID", hint: "EPIC / election card" },
};
const DEFAULT_IDS: IdType[] = ["aadhaar_digilocker", "pan", "driving_license", "passport", "voter_id"];

/**
 * Check-in router. ID verification ON → OTP or government-ID/liveness, per the platform's
 * configured method. OFF → a plain device-readiness gate (DeviceReadyCheckin) that shows NO
 * identity claims — we never put a verification screen (even a simulated one) in front of a
 * candidate for a feature that isn't running. All three still transition state so the
 * interview can start.
 */
export function CheckinStep({
  token,
  idvEnabled,
  idvMethod,
  allowedIdTypes,
}: {
  token: string;
  idvEnabled: boolean;
  idvMethod: IdVerificationMethod;
  allowedIdTypes?: IdType[];
}): ReactNode {
  if (!idvEnabled) return <DeviceReadyCheckin token={token} />;
  return idvMethod === "otp" ? (
    <OtpCheckin token={token} />
  ) : (
    <IdVerifyCheckin token={token} allowedIdTypes={allowedIdTypes} />
  );
}

/** Shown when ID verification is OFF — confirm camera & mic, then start. No ID, no signals. */
function DeviceReadyCheckin({ token }: { token: string }): ReactNode {
  const queryClient = useQueryClient();
  const cam = useCameraPreview();
  const mic = useMicLevel();
  const start = useMutation({
    mutationFn: () => api.post<{ step?: string }>(`/invite/${token}/checkin`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["invite", token] }),
  });
  return (
    <div className="cx-step">
      <h1 className="t1c-h1">Camera &amp; mic check</h1>
      <p className="mut t1c-lede">
        A quick camera &amp; mic check, then Asha — our AI interviewer, not a person — begins the interview. Find a quiet spot with a
        stable connection and about 30 uninterrupted minutes.
      </p>
      <div className="cx-checkin-grid" style={{ margin: "16px 0" }}>
        <div>
          <div className="iv-cam" style={{ position: "relative" }}>
            <video ref={cam.videoRef} className="iv-video" autoPlay playsInline muted />
            {cam.active ? (
              <div className="rec live">
                <span className="dot" /> Camera on
              </div>
            ) : (
              <div className="rec">
                <span className="dot" /> {cam.denied ? "Camera off" : "Starting camera…"}
              </div>
            )}
            {!cam.active && <div className="face" />}
          </div>
          <CameraHelp cam={cam} />
          <div className="banner info" style={{ marginTop: 12 }}>
            <Info />
            <div>
              {cam.active
                ? "Your camera works — you're ready to go. Your interview is assessed on your answers, never your video."
                : cam.denied
                  ? cameraReasonText(cam.reason)
                  : "Checking your camera… you can continue as soon as you're ready."}
            </div>
          </div>
        </div>
        <div>
          <HardwareDiagnostics camActive={cam.active} camDenied={cam.denied} mic={mic} />
        </div>
      </div>

      {start.isError && (
        <div className="banner crit" style={{ margin: "14px 0" }}>
          <Info />
          <span>Couldn't start just yet — please retry. Nothing is held against you.</span>
        </div>
      )}
      <div className="t1c-actions t1c-checkin-actions">
        <button
          className="btn primary"
          disabled={start.isPending}
          onClick={() => {
            unlockSpeech(); // iOS: warm up Asha's voice inside this tap so the first question is audible
            mic.stop();
            start.mutate();
          }}
        >
          <Check /> {start.isPending ? "Starting…" : "All systems ready · Launch AI interview"} <Arrow />
        </button>
        <span className="t1c-human">
          <RequestHuman token={token} />
        </span>
      </div>
    </div>
  );
}

/** Identity verification via a 6-digit email OTP — genuine proof of email control, real (never
 *  simulated) on a pass, verified server-side by inviteCheckin.ts against candidate_otps. */
function OtpCheckin({ token }: { token: string }): ReactNode {
  const queryClient = useQueryClient();
  const cam = useCameraPreview();
  const mic = useMicLevel();
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // A code that reached the candidate once keeps the entry field on screen even if a LATER resend
  // fails terminally — they may still be holding a valid code, and hiding the box would strand it.
  const [issuedOnce, setIssuedOnce] = useState(false);
  // Held in state, not read off send.data: react-query clears a mutation's data the moment the next
  // attempt starts, and the test-org hint must not blink out just because a resend was throttled.
  const [testOrg, setTestOrg] = useState(false);
  const [notDelivered, setNotDelivered] = useState(false);

  const send = useMutation({
    mutationFn: () => api.post<OtpSendResponse>(`/invite/${token}/checkin/otp/send`),
    onSuccess: (res) => {
      setIssuedOnce(true);
      setTestOrg(res.testOrg);
      setNotDelivered(res.emailQueued === false);
      setCooldown(SEND_THROTTLE_SECONDS);
    },
  });

  useEffect(() => {
    if (!send.isSuccess && !send.isIdle) return;
    if (send.isIdle) {
      // Auto-send on first mount so the candidate doesn't need an extra click to get a code.
      send.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cooldown > 0]);

  const verify = useMutation({
    mutationFn: () => api.post<{ step?: string }>(`/invite/${token}/checkin`, { code }),
    onSuccess: () => {
      unlockSpeech();
      mic.stop();
      void queryClient.invalidateQueries({ queryKey: ["invite", token] });
    },
  });

  const sendFailure = send.isError ? describeSendFailure(send.error) : null;
  // A terminal failure before any code ever landed: the entry field would be furniture, so the
  // screen stops asking for something the candidate cannot get and points at a human instead.
  const deadEnded = sendFailure?.kind === "terminal" && !issuedOnce;

  const verifyErrorMsg =
    verify.isError && verify.error instanceof ApiError && verify.error.status === 400
      ? "That code is invalid or has expired."
      : verify.isError
        ? "Couldn't verify just now. Please retry."
        : null;

  return (
    <div className="cx-step">
      <h1 className="t1c-h1">Camera check &amp; email code</h1>
      <p className="mut t1c-lede">
        Make sure your camera and mic work, then enter the 6-digit code we emailed you — it confirms
        you control this inbox, not your identity.
      </p>

      <div className="cx-checkin-grid" style={{ margin: "16px 0" }}>
        <div>
          <div className="iv-cam">
            <video ref={cam.videoRef} className="iv-video" autoPlay playsInline muted />
            <div className={`rec ${cam.active && !verify.isPending && !verify.isSuccess ? "live" : ""}`}>
              <span className="dot" /> {verify.isSuccess ? "Code confirmed" : cam.active ? "Camera on" : cam.denied ? "Camera off" : "Starting camera…"}
            </div>
            {!cam.active && <div className="face" />}
          </div>
          <CameraHelp cam={cam} />

          <div className="banner info" style={{ margin: "12px 0 14px" }}>
            <Info />
            <div>
              We verify a one-time code sent to your invite email — proof you control that address.{" "}
              <b>This step asks for no ID document.</b> The interview recording itself is covered by the consent
              you gave on the previous step.
            </div>
          </div>

          {/* No code can ever arrive on this path — say why, and hand over a route to a human
              instead of an input box and a retry button that cannot help. */}
          {deadEnded && sendFailure && (
            <div className="banner crit" style={{ margin: "12px 0 14px" }}>
              <Info />
              <div>
                {sendFailure.message}
                <div style={{ marginTop: 8 }}>
                  <WayOut wayOut={sendFailure.wayOut ?? "recruiter"} />
                </div>
              </div>
            </div>
          )}

          {!verify.isSuccess && !deadEnded && (
            <div className="card pad" style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <label htmlFor="cx-otp" style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>Enter the 6-digit code</label>
                <span className="mut sm" aria-live="polite">
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : (
                    <button className="btn sm ghost" disabled={send.isPending} onClick={() => send.mutate()}>
                      {send.isPending ? "Sending…" : "Resend code"}
                    </button>
                  )}
                </span>
              </div>
              <OtpBoxes value={code} onChange={setCode} />
              {/* Test orgs (0041): the code is known, so we show it rather than pretending it is a
                  secret — but we never autofill it, so a demo still runs like the real thing. */}
              {testOrg && (
                <div className="disclaimer" style={{ marginTop: 8 }}>
                  Test organisation — code: <b className="tnum">{TEST_ORG_OTP_CODE}</b>. This is a
                  demonstration tenant; a real interview's code is never shown on screen.
                </div>
              )}
              {notDelivered && (
                <div className="mut sm" style={{ marginTop: 8, color: "var(--warn)" }}>
                  Your code was created, but we couldn't send the email just now — try <b>Resend code</b> in a
                  moment, or ask your recruiter.
                </div>
              )}
              {sendFailure && (
                <div className="mut sm" style={{ marginTop: 8, color: "var(--crit)" }}>
                  {sendFailure.message}
                </div>
              )}
            </div>
          )}

          {verifyErrorMsg && (
            <div className="banner crit">
              <Info />
              <span>{verifyErrorMsg}</span>
            </div>
          )}
        </div>
        <div>{!verify.isSuccess && <HardwareDiagnostics camActive={cam.active} camDenied={cam.denied} mic={mic} />}</div>
      </div>

      <div className="t1c-actions t1c-checkin-actions">
        {!verify.isSuccess ? (
          <button
            className="btn primary"
            disabled={code.length !== 6 || verify.isPending}
            onClick={() => verify.mutate()}
          >
            <Check /> {verify.isPending ? "Verifying…" : code.length === 6 ? "Verify code & continue" : "Enter the 6-digit code"}
          </button>
        ) : (
          <button className="btn primary" disabled>
            <Check /> Confirmed — starting interview <Arrow />
          </button>
        )}
        <span className="t1c-human">
          <RequestHuman token={token} />
        </span>
      </div>
    </div>
  );
}

function IdVerifyCheckin({
  token,
  allowedIdTypes: configured,
}: {
  token: string;
  allowedIdTypes?: IdType[];
}): ReactNode {
  const queryClient = useQueryClient();
  const cam = useCameraPreview();
  const mic = useMicLevel();
  const [idType, setIdType] = useState<IdType | null>(null);
  const [revealed, setRevealed] = useState(0);

  // Offer exactly the IDs the Owner accepts for this org (from the invite payload); fall back to
  // the full default set only if the API didn't surface a list.
  const allowedIdTypes = configured && configured.length > 0 ? configured : DEFAULT_IDS;

  const checkin = useMutation({
    mutationFn: (chosen: IdType) => api.post<CheckinResponse>(`/invite/${token}/checkin`, { idType: chosen }),
  });
  const checks = checkin.data?.checks ?? [];
  // `simulated` is the genuine per-run signal from the check-in response — set only when the ID
  // provider actually ran in simulation. Never inferred before the check runs.
  const simulated = checkin.data?.simulated ?? false;

  // Stagger-reveal the returned checks so verification reads as a live process.
  useEffect(() => {
    if (checkin.isSuccess && revealed < checks.length) {
      const t = setTimeout(() => setRevealed((r) => r + 1), 450);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [checkin.isSuccess, revealed, checks.length]);

  const allRevealed = checkin.isSuccess && revealed >= checks.length;
  const running = checkin.isPending || checkin.isSuccess;

  return (
    <div className="cx-step">
      <h1 className="t1c-h1">Camera check &amp; identity verification</h1>
      <p className="mut t1c-lede">
        Make sure your camera and mic work, then a one-time identity check with any government ID.
      </p>

      <div className="cx-checkin-grid" style={{ margin: "16px 0" }}>
        <div>
          <div className="iv-cam">
            <video ref={cam.videoRef} className="iv-video" autoPlay playsInline muted />
            <div className={`rec ${cam.active && !checkin.isPending && !checkin.isSuccess ? "live" : ""}`}>
              <span className="dot" />{" "}
              {checkin.isPending
                ? "Verifying…"
                : checkin.isSuccess
                  ? "Verified"
                  : cam.active
                    ? "Camera on"
                    : cam.denied
                      ? "Camera off"
                      : "Starting camera…"}
            </div>
            {!cam.active && <div className="face" />}
          </div>
          <CameraHelp cam={cam} />

          <div className="banner info" style={{ margin: "12px 0 14px" }}>
            <Info />
            <div>
              We match your face to your ID and check you're live. <b>We store only the verified result — never your ID
              number.</b>
            </div>
          </div>

          {/* ID picker — hidden once the check is running. */}
          {!running && (
            <div style={{ margin: "4px 0 14px" }}>
              <div className="mut sm" style={{ fontWeight: 600, marginBottom: 8 }}>
                Verify with any government ID
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {allowedIdTypes.map((t) => {
                  const meta = ID_LABEL[t];
                  const on = idType === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setIdType(t)}
                      aria-pressed={on}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        textAlign: "left",
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: `1.5px solid ${on ? "var(--teal)" : "var(--line)"}`,
                        background: on ? "var(--teal-wash)" : "var(--surface)",
                        color: on ? "var(--teal-ink)" : "var(--ink)",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          border: `2px solid ${on ? "var(--teal)" : "var(--line)"}`,
                          background: on ? "var(--teal)" : "transparent",
                          display: "grid",
                          placeItems: "center",
                          flex: "none",
                        }}
                      >
                        {on && <RadioTick />}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "13.6px" }}>{meta.label}</div>
                        <div className="mut sm">{meta.hint}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(checkin.isPending || checkin.isSuccess) && (
            <div style={{ margin: "4px 0 14px" }}>
              {checkin.isPending && <div className="card pad mut sm">Running identity &amp; liveness checks…</div>}
              {checks.slice(0, revealed).map((c) => (
                <div
                  key={c.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 0",
                    borderBottom: "1px solid var(--line-soft)",
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: c.verdict === "pass" ? "var(--good-wash)" : "var(--warn-wash)",
                      color: c.verdict === "pass" ? "var(--good)" : "var(--warn)",
                      display: "grid",
                      placeItems: "center",
                      flex: "none",
                    }}
                  >
                    <ShieldCheck />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: "13.4px" }}>{c.label}</div>
                    {c.detail && <div className="mut sm">{c.detail}</div>}
                  </div>
                  <span className={`pill ${c.verdict === "pass" ? "v" : c.verdict === "flag" ? "r" : "f"}`}>
                    {c.verdict === "pass" ? "Pass" : c.verdict.replace(/_/g, " ")}
                  </span>
                </div>
              ))}
              {/* Honesty contract: when the provider is 'sim', say a demo check ran — never claim a real one. */}
              {allRevealed && simulated && (
                <div className="disclaimer" style={{ marginTop: 12 }}>
                  Demo verification — full ID check enabled by your admin. This run used a simulated provider, so the
                  result above is a demonstration, not a real government-ID match.
                </div>
              )}
            </div>
          )}

          {checkin.isError && (
            <div className="banner crit">
              <Info />
              <span>The identity check didn't complete. Please retry — nothing is held against you.</span>
            </div>
          )}
        </div>
        <div>{!running && <HardwareDiagnostics camActive={cam.active} camDenied={cam.denied} mic={mic} />}</div>
      </div>

      <div className="t1c-actions t1c-checkin-actions">
        {!allRevealed && (
          <button
            className="btn primary"
            disabled={idType === null || checkin.isPending || checkin.isSuccess}
            onClick={() => idType && checkin.mutate(idType)}
          >
            <Check />{" "}
            {checkin.isPending || checkin.isSuccess
              ? "Verifying…"
              : idType
                ? "Verify my ID"
                : "Pick an ID to continue"}
          </button>
        )}
        {allRevealed && (
          <button
            className="btn primary"
            onClick={() => {
              unlockSpeech(); // iOS: unlock Asha's voice within this tap
              mic.stop();
              void queryClient.invalidateQueries({ queryKey: ["invite", token] });
            }}
          >
            All systems ready · Launch AI interview <Arrow />
          </button>
        )}
        <span className="t1c-human">
          <RequestHuman token={token} />
        </span>
      </div>
    </div>
  );
}

/** Camera-off guidance under the preview: what happened, how to fix it, and a retry. The camera is
 *  optional, so it always says the candidate can carry on. Presentational only. */
function CameraHelp({ cam }: { cam: ReturnType<typeof useCameraPreview> }): ReactNode {
  if (cam.active || !cam.denied) return null;
  return (
    <div className="t1c-camhelp">
      <div>
        <b>Camera off.</b> {cameraReasonText(cam.reason)}
        {cam.reason === "denied" && (
          <div className="mut sm" style={{ marginTop: 4 }}>
            To allow it: tap the lock or camera icon in your address bar, set Camera to Allow, then try again.
          </div>
        )}
      </div>
      <button type="button" className="btn sm ghost" onClick={cam.retry}>
        Try again
      </button>
    </div>
  );
}

/**
 * Segmented 6-digit code entry (audit 31). One real input carries the value — so paste, SMS/email
 * autofill (one-time-code) and screen readers all work — with six boxes drawn over it.
 */
function OtpBoxes({ value, onChange }: { value: string; onChange: (v: string) => void }): ReactNode {
  const [focused, setFocused] = useState(false);
  return (
    <div className="t1c-otp">
      <input
        id="cx-otp"
        className="t1c-otp-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        aria-label="6-digit verification code"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      />
      <div className="t1c-otp-boxes" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <span
            key={i}
            className={`t1c-otp-box${value[i] ? " filled" : ""}${focused && i === Math.min(value.length, 5) ? " cur" : ""}`}
          >
            {value[i] ?? ""}
          </span>
        ))}
      </div>
    </div>
  );
}
